import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProjectService } from '../services/projectService';
import { ClientService } from '../services/clientService';
import { Material, Moment, Asset, ModifierApplication, Client } from '../types';
import { calculateMaterialLineItem, calculateProjectTotals, formatCurrency } from '../utils/calculations';
import { MaterialSelectorModal } from '../components/MaterialSelectorModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ImageGallery } from '../components/ImageGallery';
import { ImageUpload } from '../components/ImageUpload';
import { Icon } from '../components/Icon';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import '../styles/CreateQuotePage.css';

export const CreateQuotePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Project-level state
  const [projectName, setProjectName] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectDiscount, setProjectDiscount] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [taxRate, setTaxRate] = useState(0);

  // Client state
  const [clients, setClients] = useState<Client[]>([]);
  const [showClientModal, setShowClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Date fields
  const [eventDate, setEventDate] = useState<Date | null>(null);
  const [installStartDate, setInstallStartDate] = useState<Date | null>(null);
  const [installEndDate, setInstallEndDate] = useState<Date | null>(null);
  const [revisionDeadline, setRevisionDeadline] = useState<Date | null>(null);

  // Default expiration date: 30 days from now
  const getDefaultExpirationDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  };
  const [expirationDate, setExpirationDate] = useState<Date | null>(getDefaultExpirationDate());

  // Date picker refs
  const eventDateRef = useRef<DatePicker>(null);
  const installStartDateRef = useRef<DatePicker>(null);
  const installEndDateRef = useRef<DatePicker>(null);
  const revisionDeadlineRef = useRef<DatePicker>(null);
  const expirationDateRef = useRef<DatePicker>(null);

  // Quote structure
  const [moments, setMoments] = useState<Moment[]>([]);
  const [expandedMoments, setExpandedMoments] = useState<Set<string>>(new Set());
  const [editingMomentId, setEditingMomentId] = useState<string | null>(null);
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [momentNameEdits, setMomentNameEdits] = useState<Record<string, string>>({});
  const [assetNameEdits, setAssetNameEdits] = useState<Record<string, string>>({});

  // Modal state
  const [showMaterialSelector, setShowMaterialSelector] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedMomentId, setSelectedMomentId] = useState<string | null>(null);
  const [showAddMomentModal, setShowAddMomentModal] = useState(false);
  const [newMomentName, setNewMomentName] = useState('');
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [newAssetName, setNewAssetName] = useState('');
  const [addingAssetToMoment, setAddingAssetToMoment] = useState<string | null>(null);
  const [newAssetInternalPaint, setNewAssetInternalPaint] = useState(false);
  const [newAssetInternalPaintDate, setNewAssetInternalPaintDate] = useState<Date | null>(null);
  const [newAssetExternalPaint, setNewAssetExternalPaint] = useState(false);
  const [newAssetExternalPaintDate, setNewAssetExternalPaintDate] = useState<Date | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drag-and-drop handlers
  const handleDragEndMoments = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setMoments((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update order property
        return newItems.map((item, index) => ({ ...item, order: index }));
      });
    }
  };

  // Load clients on mount
  useEffect(() => {
    const loadClients = async () => {
      try {
        const data = await ClientService.getAllClients();
        setClients(data);
      } catch (err: any) {
        console.error('Failed to load clients:', err);
      }
    };
    loadClients();
  }, []);

  // Close client dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('#client') && !target.closest('.client-dropdown')) {
        setShowClientDropdown(false);
      }
    };

    if (showClientDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClientDropdown]);

  // Handle creating a new client
  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      setError('Client name is required');
      return;
    }

    try {
      const newClient = await ClientService.createClient(
        { name: newClientName.trim() },
        user!.uid
      );
      setClients([...clients, newClient]);
      setSelectedClientId(newClient.id);
      setNewClientName('');
      setShowClientModal(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddMoment = () => {
    setShowAddMomentModal(true);
  };

  const handleCreateMoment = () => {
    if (!newMomentName.trim()) {
      setError('Moment name is required');
      return;
    }

    const momentId = `moment_${Date.now()}`;
    const newMoment: Moment = {
      id: momentId,
      name: newMomentName.trim(),
      description: '',
      order: moments.length,
      assets: [],
    };
    setMoments([...moments, newMoment]);
    setExpandedMoments(new Set(expandedMoments).add(momentId));
    setNewMomentName('');
    setShowAddMomentModal(false);
  };

  const handleUpdateMoment = (momentId: string, updates: Partial<Moment>) => {
    setMoments(
      moments.map((m) =>
        m.id === momentId ? { ...m, ...updates } : m
      )
    );
  };

  const handleDeleteMoment = (momentId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Moment',
      message: 'Are you sure you want to delete this moment? All assets and materials will be removed.',
      onConfirm: () => {
        setMoments(moments.filter((m) => m.id !== momentId));
        const updated = new Set(expandedMoments);
        updated.delete(momentId);
        setExpandedMoments(updated);
        setEditingMomentId(null);
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      },
    });
  };

  const toggleMomentExpand = (momentId: string) => {
    const updated = new Set(expandedMoments);
    if (updated.has(momentId)) {
      updated.delete(momentId);
    } else {
      updated.add(momentId);
    }
    setExpandedMoments(updated);
  };

  const handleAddAsset = (momentId: string) => {
    setAddingAssetToMoment(momentId);
    setShowAddAssetModal(true);
  };

  const handleCreateAsset = () => {
    if (!newAssetName.trim()) {
      setError('Asset name is required');
      return;
    }

    if (!addingAssetToMoment) return;

    const assetId = `asset_${Date.now()}`;
    const newAsset: Asset = {
      id: assetId,
      name: newAssetName.trim(),
      description: '',
      order: (getMoment(addingAssetToMoment)?.assets.length || 0),
      materials: [],
      laborItems: [],
      vinylItems: [],
      printingItems: [],
      internalPaint: newAssetInternalPaint,
      internalPaintDate: newAssetInternalPaintDate,
      externalPaintShop: newAssetExternalPaint,
      externalPaintDropOffDate: newAssetExternalPaintDate,
    };

    setMoments(
      moments.map((m) =>
        m.id === addingAssetToMoment
          ? { ...m, assets: [...(m.assets || []), newAsset] }
          : m
      )
    );

    setNewAssetName('');
    setAddingAssetToMoment(null);
    setNewAssetInternalPaint(false);
    setNewAssetInternalPaintDate(null);
    setNewAssetExternalPaint(false);
    setNewAssetExternalPaintDate(null);
    setShowAddAssetModal(false);
  };

  const handleUpdateAsset = (momentId: string, assetId: string, updates: Partial<Asset>) => {
    setMoments(
      moments.map((m) =>
        m.id === momentId
          ? {
              ...m,
              assets: m.assets.map((a) =>
                a.id === assetId ? { ...a, ...updates } : a
              ),
            }
          : m
      )
    );
  };

  const handleDeleteAsset = (momentId: string, assetId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Asset',
      message: 'Are you sure you want to delete this asset? All materials will be removed.',
      onConfirm: () => {
        setMoments(
          moments.map((m) =>
            m.id === momentId
              ? { ...m, assets: m.assets.filter((a) => a.id !== assetId) }
              : m
          )
        );
        setEditingAssetId(null);
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      },
    });
  };

  const handleAddMaterial = (material: Material, quantity: number, modifiers: ModifierApplication[]) => {
    if (!selectedMomentId || !selectedAssetId) return;

    const calculation = calculateMaterialLineItem(material, quantity, modifiers);
    const lineItemId = `line_${Date.now()}`;

    const lineItem = {
      id: lineItemId,
      materialId: material.id,
      materialName: material.name,
      materialBasePrice: material.basePrice,
      markupFactor: material.markupFactor,
      quantity,
      modifiers,
      unitCostBeforeMarkup: calculation.unitCostBeforeMarkup,
      unitCostAfterMarkup: calculation.unitCostAfterMarkup,
      totalCostBeforeMarkup: calculation.totalCostBeforeMarkup,
      totalCostAfterMarkup: calculation.totalCostAfterMarkup,
      calculatedBreakdown: calculation.breakdown,
    };

    setMoments(
      moments.map((m) =>
        m.id === selectedMomentId
          ? {
              ...m,
              assets: m.assets.map((a) =>
                a.id === selectedAssetId
                  ? { ...a, materials: [...(a.materials || []), lineItem] }
                  : a
              ),
            }
          : m
      )
    );

    setShowMaterialSelector(false);
  };

  const handleDeleteMaterial = (momentId: string, assetId: string, materialId: string) => {
    setMoments(
      moments.map((m) =>
        m.id === momentId
          ? {
              ...m,
              assets: m.assets.map((a) =>
                a.id === assetId
                  ? { ...a, materials: a.materials.filter((mat) => mat.id !== materialId) }
                  : a
              ),
            }
          : m
      )
    );
  };

  const getMoment = (momentId: string) => moments.find((m) => m.id === momentId);

  const getSelectedClient = () => clients.find((c) => c.id === selectedClientId);

  const getFilteredClients = () => {
    if (!clientSearchTerm.trim()) return clients;
    const search = clientSearchTerm.toLowerCase();
    return clients.filter((client) =>
      client.name.toLowerCase().includes(search) ||
      (client.company && client.company.toLowerCase().includes(search))
    );
  };

  const handleSelectClient = (client: Client) => {
    setSelectedClientId(client.id);
    setClientSearchTerm('');
    setShowClientDropdown(false);
  };

  const calculateAssetTotal = (asset: Asset): number => {
    return (asset.materials || []).reduce((sum, material) => sum + material.totalCostAfterMarkup, 0);
  };

  const calculateMomentTotal = (moment: Moment): number => {
    return (moment.assets || []).reduce((sum, asset) => sum + calculateAssetTotal(asset), 0);
  };

  const calculateTotal = () => {
    let subtotal = 0;
    moments.forEach((moment) => {
      subtotal += calculateMomentTotal(moment);
    });

    return calculateProjectTotals(subtotal, projectDiscount, shippingCost, taxRate);
  };

  const totals = calculateTotal();

  const handleSaveQuote = async () => {
    if (!user?.uid) return;
    if (!projectName.trim() || !selectedClientId) {
      setError('Project name and client are required');
      return;
    }

    const selectedClient = clients.find(c => c.id === selectedClientId);
    if (!selectedClient) {
      setError('Selected client not found');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await ProjectService.createProject({
        name: projectName,
        description: projectDescription,
        clientId: selectedClientId,
        clientName: selectedClient.name,
        status: 'draft',
        createdBy: user.uid,
        projectLevelDiscount: projectDiscount,
        shippingCost,
        taxRate,
        moments,
        eventDate: eventDate || undefined,
        installStartDate: installStartDate || undefined,
        installEndDate: installEndDate || undefined,
        revisionDeadline: revisionDeadline || undefined,
        expirationDate: expirationDate || undefined,
      });

      navigate('/quotes');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!user?.uid) return;
    if (!projectName.trim() || !selectedClientId) {
      setError('Project name and client are required');
      return;
    }

    const selectedClient = clients.find(c => c.id === selectedClientId);
    if (!selectedClient) {
      setError('Selected client not found');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const newProject = await ProjectService.createProject({
        name: projectName,
        description: projectDescription,
        clientId: selectedClientId,
        clientName: selectedClient.name,
        status: 'draft',
        createdBy: user.uid,
        projectLevelDiscount: projectDiscount,
        shippingCost,
        taxRate,
        moments,
        eventDate: eventDate || undefined,
        installStartDate: installStartDate || undefined,
        installEndDate: installEndDate || undefined,
        revisionDeadline: revisionDeadline || undefined,
        expirationDate: expirationDate || undefined,
      });

      alert('Draft saved! You can continue editing it later.');
      navigate('/quotes');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sortable wrappers
  const SortableItem = ({ id, children }: { id: string; children: React.ReactNode }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style}>
        <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'inline-block', marginRight: '8px', padding: '4px' }}>
          ⋮⋮
        </div>
        {children}
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="create-quote-page">
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Quotes', path: '/quotes' },
              { label: 'New Quote' },
            ]}
          />
        </div>

        <div className="header-content">
          <div>
            <h1>Create New Quote</h1>
            <p>Build and customize your quote</p>
          </div>
          <div className="header-actions">
            <button onClick={() => navigate('/quotes')} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <strong>Error:</strong> {error}
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}

      <div className="quote-container">
        <div className="quote-main">
          {/* Project Info Section */}
          <section className="section">
            <h2 className="section-title">Project Information</h2>

            <div className="form-group">
              <label htmlFor="projectName">Project Name*</label>
              <input
                type="text"
                id="projectName"
                placeholder="e.g., Custom Signage for Main Street"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="client">Client*</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    id="client"
                    placeholder={getSelectedClient() ? `${getSelectedClient()!.name}${getSelectedClient()!.company ? ` (${getSelectedClient()!.company})` : ''}` : 'Search for a client...'}
                    value={clientSearchTerm}
                    onChange={(e) => {
                      setClientSearchTerm(e.target.value);
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    required={!selectedClientId}
                    style={{
                      width: '100%',
                      backgroundColor: selectedClientId && !clientSearchTerm ? 'var(--card-background)' : 'var(--main-background)'
                    }}
                  />
                  {selectedClientId && !clientSearchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedClientId('');
                        setClientSearchTerm('');
                        setShowClientDropdown(true);
                      }}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--secondary-text)',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '0 4px'
                      }}
                    >
                      ×
                    </button>
                  )}
                  {showClientDropdown && (clientSearchTerm || !selectedClientId) && (
                    <div
                      className="client-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: 'var(--card-background)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        marginTop: '4px',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                    >
                      {getFilteredClients().length === 0 ? (
                        <div style={{ padding: '12px', color: 'var(--secondary-text)', textAlign: 'center' }}>
                          No clients found
                        </div>
                      ) : (
                        getFilteredClients().map((client) => (
                          <div
                            key={client.id}
                            onClick={() => handleSelectClient(client)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--border-color)',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--secondary-background)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div style={{ fontWeight: '500' }}>{client.name}</div>
                            {client.company && (
                              <div style={{ fontSize: '12px', color: 'var(--secondary-text)', marginTop: '2px' }}>
                                {client.company}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowClientModal(true)}
                  className="btn btn-secondary"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  + New Client
                </button>
              </div>
              <small style={{ color: 'var(--secondary-text)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Search for an existing client or create a new one
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="projectDescription">Description</label>
              <textarea
                id="projectDescription"
                placeholder="Add any special notes or requirements..."
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="dates-grid">
              <div className="form-group">
                <label htmlFor="eventDate">Event Date</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DatePicker
                    ref={eventDateRef}
                    selected={eventDate}
                    onChange={(date: Date | null) => setEventDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select date"
                    className="form-control"
                    id="eventDate"
                    calendarClassName="custom-calendar"
                  />
                  <div onClick={() => eventDateRef.current?.setFocus()} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Icon name="calendar" size={18} color="var(--secondary-text)" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="installStartDate">Install Start</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DatePicker
                    ref={installStartDateRef}
                    selected={installStartDate}
                    onChange={(date: Date | null) => setInstallStartDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select date"
                    className="form-control"
                    id="installStartDate"
                    calendarClassName="custom-calendar"
                  />
                  <div onClick={() => installStartDateRef.current?.setFocus()} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Icon name="calendar" size={18} color="var(--secondary-text)" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="installEndDate">Install End</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DatePicker
                    ref={installEndDateRef}
                    selected={installEndDate}
                    onChange={(date: Date | null) => setInstallEndDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select date"
                    className="form-control"
                    id="installEndDate"
                    calendarClassName="custom-calendar"
                  />
                  <div onClick={() => installEndDateRef.current?.setFocus()} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Icon name="calendar" size={18} color="var(--secondary-text)" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="revisionDeadline">Revision Deadline</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DatePicker
                    ref={revisionDeadlineRef}
                    selected={revisionDeadline}
                    onChange={(date: Date | null) => setRevisionDeadline(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select date"
                    className="form-control"
                    id="revisionDeadline"
                    calendarClassName="custom-calendar"
                  />
                  <div onClick={() => revisionDeadlineRef.current?.setFocus()} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Icon name="calendar" size={18} color="var(--secondary-text)" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="expirationDate">Expiration</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DatePicker
                    ref={expirationDateRef}
                    selected={expirationDate}
                    onChange={(date: Date | null) => setExpirationDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select date"
                    className="form-control"
                    id="expirationDate"
                    calendarClassName="custom-calendar"
                  />
                  <div onClick={() => expirationDateRef.current?.setFocus()} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Icon name="calendar" size={18} color="var(--secondary-text)" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Moments/Assets/Materials Section */}
          <section className="section">
            <div className="section-header">
              <h2 className="section-title">Quote Items</h2>
              <button onClick={handleAddMoment} className="btn btn-primary btn-small">
                + Add Moment
              </button>
            </div>

            {moments.length === 0 ? (
              <div className="empty-placeholder">
                <p>No moments yet. Click "Add Moment" to start building your quote.</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEndMoments}
              >
                <SortableContext
                  items={moments.map((m) => m.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="moments-list">
                    {moments.map((moment) => (
                      <SortableItem key={moment.id} id={moment.id}>
                        <div className="moment-card">
                          <div className="moment-header">
                            <button
                              className="expand-button"
                              onClick={() => toggleMomentExpand(moment.id)}
                            >
                              {expandedMoments.has(moment.id) ? '▼' : '▶'} {moment.name}
                            </button>
                      <div className="moment-actions">
                        {editingMomentId === moment.id ? (
                          <>
                            <input
                              type="text"
                              className="moment-name-input"
                              value={momentNameEdits[moment.id] !== undefined ? momentNameEdits[moment.id] : moment.name}
                              onChange={(e) =>
                                setMomentNameEdits({ ...momentNameEdits, [moment.id]: e.target.value })
                              }
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                            <button
                              onClick={() => {
                                handleUpdateMoment(moment.id, { name: momentNameEdits[moment.id] || moment.name });
                                setEditingMomentId(null);
                              }}
                              className="btn btn-primary btn-small"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setMomentNameEdits({ ...momentNameEdits, [moment.id]: '' });
                                setEditingMomentId(null);
                              }}
                              className="btn btn-secondary btn-small"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setMomentNameEdits({ ...momentNameEdits, [moment.id]: moment.name });
                                setEditingMomentId(moment.id);
                              }}
                              className="btn btn-secondary btn-small"
                            >
                              Rename
                            </button>
                            <button
                              onClick={() => handleDeleteMoment(moment.id)}
                              className="btn btn-danger btn-small"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {expandedMoments.has(moment.id) && (
                      <div className="moment-content">
                        {/* Moment Photos */}
                        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--card-background)', borderRadius: '6px' }}>
                          <h4 style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '600' }}>Moment Photos</h4>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {/* Existing photos with remove button */}
                            {(moment.photos || []).map((photo, index) => (
                              <div
                                key={index}
                                style={{
                                  position: 'relative',
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '6px',
                                  overflow: 'hidden',
                                  border: '1px solid var(--border-color)',
                                  cursor: 'pointer'
                                }}
                              >
                                <img
                                  src={photo}
                                  alt={`Moment ${index + 1}`}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newPhotos = [...(moment.photos || [])];
                                    newPhotos.splice(index, 1);
                                    handleUpdateMoment(moment.id, { photos: newPhotos });
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    background: 'rgba(220, 38, 38, 0.9)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    padding: 0
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            {/* Upload slots */}
                            {(moment.photos?.length || 0) < 5 && Array.from({ length: Math.min(1, 5 - (moment.photos?.length || 0)) }).map((_, index) => (
                              <label
                                key={`upload-${index}`}
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '6px',
                                  border: '2px dashed var(--border-color)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  backgroundColor: 'var(--main-background)',
                                  transition: 'all 0.2s',
                                  fontSize: '24px',
                                  color: 'var(--secondary-text)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--primary-button)';
                                  e.currentTarget.style.backgroundColor = 'var(--card-background)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--border-color)';
                                  e.currentTarget.style.backgroundColor = 'var(--main-background)';
                                }}
                              >
                                +
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    const remainingSlots = 5 - (moment.photos?.length || 0);
                                    const filesToProcess = files.slice(0, remainingSlots);

                                    Promise.all(
                                      filesToProcess.map((file) => {
                                        return new Promise<string>((resolve, reject) => {
                                          if (file.size > 5 * 1024 * 1024) {
                                            alert(`${file.name} is too large. Max 5MB per image.`);
                                            reject();
                                            return;
                                          }
                                          const reader = new FileReader();
                                          reader.readAsDataURL(file);
                                          reader.onload = () => resolve(reader.result as string);
                                          reader.onerror = reject;
                                        });
                                      })
                                    ).then((base64Images) => {
                                      handleUpdateMoment(moment.id, {
                                        photos: [...(moment.photos || []), ...base64Images]
                                      });
                                    }).catch(() => {});

                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="assets-list">
                          {(moment.assets || []).map((asset) => (
                            <div key={asset.id} className="asset-card">
                              <div className="asset-header">
                                {editingAssetId === asset.id ? (
                                  <input
                                    type="text"
                                    className="asset-name-input"
                                    value={assetNameEdits[asset.id] !== undefined ? assetNameEdits[asset.id] : asset.name}
                                    onChange={(e) =>
                                      setAssetNameEdits({ ...assetNameEdits, [asset.id]: e.target.value })
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    autoFocus
                                  />
                                ) : (
                                  <h4>{asset.name}</h4>
                                )}
                                <div className="asset-actions">
                                  {editingAssetId === asset.id ? (
                                    <>
                                      <button
                                        onClick={() => {
                                          handleUpdateAsset(moment.id, asset.id, {
                                            name: assetNameEdits[asset.id] || asset.name,
                                          });
                                          setEditingAssetId(null);
                                        }}
                                        className="btn btn-primary btn-small"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => {
                                          setAssetNameEdits({ ...assetNameEdits, [asset.id]: '' });
                                          setEditingAssetId(null);
                                        }}
                                        className="btn btn-secondary btn-small"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => {
                                          setSelectedAssetId(asset.id);
                                          setSelectedMomentId(moment.id);
                                          setShowMaterialSelector(true);
                                        }}
                                        className="btn btn-primary btn-small"
                                      >
                                        + Add Material
                                      </button>
                                      <button
                                        onClick={() => {
                                          setAssetNameEdits({ ...assetNameEdits, [asset.id]: asset.name });
                                          setEditingAssetId(asset.id);
                                        }}
                                        className="btn btn-secondary btn-small"
                                      >
                                        Rename
                                      </button>
                                      <button
                                        onClick={() => handleDeleteAsset(moment.id, asset.id)}
                                        className="btn btn-danger btn-small"
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Asset Photos */}
                              <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: 'var(--main-background)', borderRadius: '6px' }}>
                                <h5 style={{ fontSize: '13px', marginBottom: '6px', fontWeight: '600' }}>Asset Photos</h5>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                                  {/* Existing photos with remove button */}
                                  {(asset.photos || []).map((photo, index) => (
                                    <div
                                      key={index}
                                      style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '6px',
                                        overflow: 'hidden',
                                        border: '1px solid var(--border-color)',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      <img
                                        src={photo}
                                        alt={`Asset ${index + 1}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      />
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newPhotos = [...(asset.photos || [])];
                                          newPhotos.splice(index, 1);
                                          handleUpdateAsset(moment.id, asset.id, { photos: newPhotos });
                                        }}
                                        style={{
                                          position: 'absolute',
                                          top: '4px',
                                          right: '4px',
                                          background: 'rgba(220, 38, 38, 0.9)',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          width: '20px',
                                          height: '20px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          fontWeight: 'bold',
                                          padding: 0
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                  {/* Upload slots */}
                                  {(asset.photos?.length || 0) < 5 && Array.from({ length: Math.min(1, 5 - (asset.photos?.length || 0)) }).map((_, index) => (
                                    <label
                                      key={`upload-${index}`}
                                      style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '6px',
                                        border: '2px dashed var(--border-color)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        backgroundColor: 'var(--main-background)',
                                        transition: 'all 0.2s',
                                        fontSize: '24px',
                                        color: 'var(--secondary-text)'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--primary-button)';
                                        e.currentTarget.style.backgroundColor = 'var(--card-background)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--border-color)';
                                        e.currentTarget.style.backgroundColor = 'var(--main-background)';
                                      }}
                                    >
                                      +
                                      <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                          const files = Array.from(e.target.files || []);
                                          const remainingSlots = 5 - (asset.photos?.length || 0);
                                          const filesToProcess = files.slice(0, remainingSlots);

                                          Promise.all(
                                            filesToProcess.map((file) => {
                                              return new Promise<string>((resolve, reject) => {
                                                if (file.size > 5 * 1024 * 1024) {
                                                  alert(`${file.name} is too large. Max 5MB per image.`);
                                                  reject();
                                                  return;
                                                }
                                                const reader = new FileReader();
                                                reader.readAsDataURL(file);
                                                reader.onload = () => resolve(reader.result as string);
                                                reader.onerror = reject;
                                              });
                                            })
                                          ).then((base64Images) => {
                                            handleUpdateAsset(moment.id, asset.id, {
                                              photos: [...(asset.photos || []), ...base64Images]
                                            });
                                          }).catch(() => {});

                                          e.target.value = '';
                                        }}
                                      />
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Paint Information */}
                              <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: 'var(--main-background)', borderRadius: '6px' }}>
                                <h5 style={{ fontSize: '13px', marginBottom: '8px', fontWeight: '600' }}>Paint Information</h5>

                                {/* Internal Paint */}
                                <div style={{ marginBottom: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                      type="checkbox"
                                      id={`internal-paint-${asset.id}`}
                                      checked={asset.internalPaint || false}
                                      onChange={(e) => {
                                        handleUpdateAsset(moment.id, asset.id, {
                                          internalPaint: e.target.checked,
                                          internalPaintDate: e.target.checked ? asset.internalPaintDate : null,
                                        });
                                      }}
                                      style={{ width: 'auto', cursor: 'pointer' }}
                                    />
                                    <label htmlFor={`internal-paint-${asset.id}`} style={{ margin: 0, cursor: 'pointer', fontSize: '13px' }}>
                                      Internal Paint
                                    </label>
                                  </div>
                                  {asset.internalPaint && (
                                    <div style={{ marginTop: '6px', marginLeft: '24px' }}>
                                      <DatePicker
                                        selected={asset.internalPaintDate ? (asset.internalPaintDate instanceof Date ? asset.internalPaintDate : (typeof (asset.internalPaintDate as any).toDate === 'function' ? (asset.internalPaintDate as any).toDate() : new Date(asset.internalPaintDate as any))) : null}
                                        onChange={(date: Date | null) => {
                                          handleUpdateAsset(moment.id, asset.id, {
                                            internalPaintDate: date,
                                          });
                                        }}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="Select paint date"
                                        className="form-control"
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* External Paint Shop */}
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                      type="checkbox"
                                      id={`external-paint-${asset.id}`}
                                      checked={asset.externalPaintShop || false}
                                      onChange={(e) => {
                                        handleUpdateAsset(moment.id, asset.id, {
                                          externalPaintShop: e.target.checked,
                                          externalPaintDropOffDate: e.target.checked ? asset.externalPaintDropOffDate : null,
                                        });
                                      }}
                                      style={{ width: 'auto', cursor: 'pointer' }}
                                    />
                                    <label htmlFor={`external-paint-${asset.id}`} style={{ margin: 0, cursor: 'pointer', fontSize: '13px' }}>
                                      External Paint Shop
                                    </label>
                                  </div>
                                  {asset.externalPaintShop && (
                                    <div style={{ marginTop: '6px', marginLeft: '24px' }}>
                                      <DatePicker
                                        selected={asset.externalPaintDropOffDate ? (asset.externalPaintDropOffDate instanceof Date ? asset.externalPaintDropOffDate : (typeof (asset.externalPaintDropOffDate as any).toDate === 'function' ? (asset.externalPaintDropOffDate as any).toDate() : new Date(asset.externalPaintDropOffDate as any))) : null}
                                        onChange={(date: Date | null) => {
                                          handleUpdateAsset(moment.id, asset.id, {
                                            externalPaintDropOffDate: date,
                                          });
                                        }}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="Select drop-off date"
                                        className="form-control"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="materials-list">
                                {(asset.materials || []).map((material) => (
                                  <div key={material.id} className="material-item">
                                    <div className="material-info">
                                      <h5>{material.materialName}</h5>
                                      <p>Qty: {material.quantity} | Markup: {(material.markupFactor - 1) * 100}%</p>
                                      {material.modifiers && material.modifiers.length > 0 && (
                                        <div className="material-processes">
                                          <span className="processes-label">Processes</span>
                                          <ul className="processes-list-inline">
                                            {material.modifiers.map((mod, idx) => (
                                              <li key={idx}>
                                                <strong>{mod.processName}</strong>
                                                <span style={{ marginLeft: '8px', color: 'var(--secondary-text)' }}>
                                                  ({mod.costType === 'flat_rate' ? 'Flat Rate' : `${mod.sqftAmount} sqft`}) - {formatCurrency(mod.costValue)}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {material.photos && material.photos.length > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                          <ImageGallery images={material.photos} size="small" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="material-price">
                                      <div className="price-row">
                                        <span>Unit</span>
                                        <span>{formatCurrency(material.unitCostAfterMarkup)}</span>
                                      </div>
                                      <div className="price-row total">
                                        <span>Total</span>
                                        <span>{formatCurrency(material.totalCostAfterMarkup)}</span>
                                      </div>
                                    </div>
                                    <div>
                                      <button
                                        onClick={() =>
                                          handleDeleteMaterial(moment.id, asset.id, material.id)
                                        }
                                        className="btn btn-danger btn-small"
                                        style={{ padding: '6px 10px' }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                ))}

                                {(asset.materials || []).length === 0 && (
                                  <p className="no-materials">
                                    No materials added. Click "Add Material" to add items.
                                  </p>
                                )}
                                {(asset.materials || []).length > 0 && (
                                  <div style={{
                                    marginTop: '16px',
                                    paddingTop: '16px',
                                    borderTop: '2px solid var(--border-color)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    fontWeight: '700',
                                    fontSize: '16px',
                                    color: 'var(--main-text)',
                                    background: 'var(--card-background)',
                                    padding: '12px 16px',
                                    borderRadius: '6px'
                                  }}>
                                    <span>{asset.name} Total</span>
                                    <span style={{ color: 'var(--primary-button)' }}>{formatCurrency(calculateAssetTotal(asset))}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          <button
                            onClick={() => handleAddAsset(moment.id)}
                            className="btn btn-secondary btn-block"
                          >
                            + Add Asset to {moment.name}
                          </button>

                          {(moment.assets || []).length > 0 && (
                            <div style={{
                              marginTop: '24px',
                              padding: '16px 20px',
                              backgroundColor: 'var(--secondary-background)',
                              borderRadius: '8px',
                              border: '2px solid var(--primary-button)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontSize: '18px',
                              fontWeight: '700',
                              color: 'var(--primary-button)'
                            }}>
                              <span>{moment.name} Total</span>
                              <span>{formatCurrency(calculateMomentTotal(moment))}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                      </SortableItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {/* Project-Level Adjustments */}
          <section className="section">
            <h2 className="section-title">Project Adjustments</h2>

            <div className="adjustments-grid">
              <div className="form-group">
                <label htmlFor="discount">Project Discount (%)</label>
                <input
                  type="number"
                  id="discount"
                  min="0"
                  max="100"
                  value={projectDiscount}
                  onChange={(e) => setProjectDiscount(parseFloat(e.target.value) || 0)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="shipping">Shipping Cost ($)</label>
                <input
                  type="number"
                  id="shipping"
                  min="0"
                  step="0.01"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(parseFloat(e.target.value) || 0)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="tax">Tax Rate (%)</label>
                <input
                  type="number"
                  id="tax"
                  min="0"
                  max="100"
                  step="0.001"
                  value={taxRate}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          </section>
        </div>

        {/* Sidebar - Totals */}
        <aside className="quote-sidebar">
          <div className="totals-card">
            <h3>Quote Summary</h3>

            <div className="total-row">
              <span>Subtotal:</span>
              <span>{formatCurrency(totals.subtotal)}</span>
            </div>

            {projectDiscount > 0 && (
              <div className="total-row discount">
                <span>Discount ({projectDiscount}%):</span>
                <span>-{formatCurrency(totals.discountAmount)}</span>
              </div>
            )}

            {totals.discountAmount > 0 && (
              <div className="total-row">
                <span>After Discount:</span>
                <span>{formatCurrency(totals.subtotalAfterDiscount)}</span>
              </div>
            )}

            {shippingCost > 0 && (
              <div className="total-row">
                <span>Shipping:</span>
                <span>+{formatCurrency(shippingCost)}</span>
              </div>
            )}

            <div className="total-row">
              <span>Subtotal Before Tax:</span>
              <span>{formatCurrency(totals.subtotalBeforeTax)}</span>
            </div>

            {taxRate > 0 && (
              <div className="total-row">
                <span>Tax ({taxRate}%):</span>
                <span>+{formatCurrency(totals.taxAmount)}</span>
              </div>
            )}

            <div className="total-row final">
              <span>TOTAL:</span>
              <span>{formatCurrency(totals.finalTotal)}</span>
            </div>

            <div className="sidebar-actions">
              <button
                onClick={handleSaveQuote}
                disabled={loading}
                className="btn btn-primary btn-block"
              >
                {loading ? 'Saving...' : 'Save Quote'}
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={loading}
                className="btn btn-secondary btn-block"
              >
                Save as Draft
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Material Selector Modal */}
      <MaterialSelectorModal
        isOpen={showMaterialSelector}
        onClose={() => setShowMaterialSelector(false)}
        onSelect={handleAddMaterial}
        momentName={getMoment(selectedMomentId || '')?.name || 'Item'}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
      />

      {/* Create Client Modal */}
      {showClientModal && (
        <div className="modal-overlay" onClick={() => setShowClientModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Client</h2>
              <button onClick={() => setShowClientModal(false)} className="modal-close">&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="newClientName">Client Name*</label>
                <input
                  type="text"
                  id="newClientName"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="e.g., John Smith"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateClient();
                    }
                  }}
                />
                <small style={{ color: 'var(--secondary-text)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  You can add more details in the Clients page later
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => setShowClientModal(false)} className="btn btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={handleCreateClient} className="btn btn-primary">
                Create Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Moment Modal */}
      {showAddMomentModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddMomentModal(false);
          setNewMomentName('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Moment</h2>
              <button onClick={() => {
                setShowAddMomentModal(false);
                setNewMomentName('');
              }} className="modal-close">&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="newMomentName">Moment Name*</label>
                <input
                  type="text"
                  id="newMomentName"
                  value={newMomentName}
                  onChange={(e) => setNewMomentName(e.target.value)}
                  placeholder="e.g., Exterior Signage, Interior Graphics, Wayfinding"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateMoment();
                    }
                  }}
                />
                <small style={{ color: 'var(--secondary-text)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Give this moment a descriptive name to organize your quote
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => {
                setShowAddMomentModal(false);
                setNewMomentName('');
              }} className="btn btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={handleCreateMoment} className="btn btn-primary">
                Add Moment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Asset Modal */}
      {showAddAssetModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddAssetModal(false);
          setNewAssetName('');
          setAddingAssetToMoment(null);
          setNewAssetInternalPaint(false);
          setNewAssetInternalPaintDate(null);
          setNewAssetExternalPaint(false);
          setNewAssetExternalPaintDate(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Asset</h2>
              <button onClick={() => {
                setShowAddAssetModal(false);
                setNewAssetName('');
                setAddingAssetToMoment(null);
                setNewAssetInternalPaint(false);
                setNewAssetInternalPaintDate(null);
                setNewAssetExternalPaint(false);
                setNewAssetExternalPaintDate(null);
              }} className="modal-close">&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="newAssetName">Asset Name*</label>
                <input
                  type="text"
                  id="newAssetName"
                  value={newAssetName}
                  onChange={(e) => setNewAssetName(e.target.value)}
                  placeholder="e.g., Main Entrance Sign, Lobby Directory, Vehicle Wrap"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateAsset();
                    }
                  }}
                />
                <small style={{ color: 'var(--secondary-text)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Give this asset a descriptive name
                </small>
              </div>

              {/* Internal Paint Checkbox */}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="internalPaint"
                    checked={newAssetInternalPaint}
                    onChange={(e) => {
                      setNewAssetInternalPaint(e.target.checked);
                      if (!e.target.checked) {
                        setNewAssetInternalPaintDate(null);
                      }
                    }}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                  <label htmlFor="internalPaint" style={{ margin: 0, cursor: 'pointer' }}>
                    Internal Paint
                  </label>
                </div>
                {newAssetInternalPaint && (
                  <div style={{ marginTop: '8px', marginLeft: '24px' }}>
                    <label htmlFor="internalPaintDate" style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>
                      Paint Date
                    </label>
                    <DatePicker
                      selected={newAssetInternalPaintDate}
                      onChange={(date: Date | null) => setNewAssetInternalPaintDate(date)}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Select paint date"
                      className="form-control"
                      id="internalPaintDate"
                    />
                  </div>
                )}
              </div>

              {/* External Paint Shop Checkbox */}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="externalPaint"
                    checked={newAssetExternalPaint}
                    onChange={(e) => {
                      setNewAssetExternalPaint(e.target.checked);
                      if (!e.target.checked) {
                        setNewAssetExternalPaintDate(null);
                      }
                    }}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                  <label htmlFor="externalPaint" style={{ margin: 0, cursor: 'pointer' }}>
                    External Paint Shop
                  </label>
                </div>
                {newAssetExternalPaint && (
                  <div style={{ marginTop: '8px', marginLeft: '24px' }}>
                    <label htmlFor="externalPaintDate" style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>
                      Drop-off Date
                    </label>
                    <DatePicker
                      selected={newAssetExternalPaintDate}
                      onChange={(date: Date | null) => setNewAssetExternalPaintDate(date)}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Select drop-off date"
                      className="form-control"
                      id="externalPaintDate"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => {
                setShowAddAssetModal(false);
                setNewAssetName('');
                setAddingAssetToMoment(null);
                setNewAssetInternalPaint(false);
                setNewAssetInternalPaintDate(null);
                setNewAssetExternalPaint(false);
                setNewAssetExternalPaintDate(null);
              }} className="btn btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={handleCreateAsset} className="btn btn-primary">
                Add Asset
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AppLayout>
  );
};
